import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import queryString from 'query-string';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import toastr from './toastr';
import { getPage, requestPage, unmountPage } from '../actions/PageActions';
import PageContainer from './PageContainer';
import Archive from './archive/Archive';
import Db from './Db';
import Report from './report/Report';
import List from './List';
import Item from './Item';
import LoadingPage from './LoadingPage';
import FileNotFound from './FileNotFound';
import Header from './Header';
import Footer from './Footer';
import CalendarLoader from './loader/CalendarLoader';
import Shopkeeper from './shopkeeper/ShopKeeper.tsx';
import TimeClock from './timeclock/TimeClock';
import SignatureLoader from './loader/SignatureLoader';
import QueryListContainer from './query/QueryListContainer';
import PrintLogRouter from './print/PrintLogRouter';
import PaymentRouter from './payment/PaymentRouter';
import UserAdminRouter from './admin/UserAdminRouter';
import TimeCard from './timecard/TimeCard';
import ConfigRouter from './config/ConfigRouter';
import SalesDashboard from './crm/SalesDashboard';
import RedisList from './db/RedisList';
import PartRequestKanban from './kanban/PartBoard';
import RepairOrderBoard from './kanban/RepairOrderBoard';

class Page extends Component {
  constructor(props) {
    super(props);

    this.state = {
      notFound: false,
      unauthorized: false,
    };
  }

  componentDidMount() {
    this.getData(this.props.match.params.pageName.toUpperCase());
  }

  componentDidUpdate() {
    const pageName = this.props.match.params.pageName.toUpperCase();

    if (pageName !== this.props.pageName) {
      // page has changed, get new page data
      this.updateHistory();
      this.getData(pageName);
    }
  }

  componentWillUnmount() {
    this.props.unmountPage(this.props.pageName);
  }

  checkConditions() {
    const conditions = this.getPageConfigValue('Conditions');
    if (!conditions) {
      return true;
    }

    for (const key of Object.keys(conditions)) {
      if (_.get(this.props.config, key) !== conditions[key]) {
        return false;
      }
    }

    return true;
  }

  getPageConfig() {
    // check for the page key first, remove check when page key ever goes away
    return _.get(this.props.pageConfig, 'page', this.props.pageConfig);
  }

  getPageConfigValue(key) {
    return _.get(this.getPageConfig(), key);
  }

  getData(pageName) {
    this.props.requestPage(pageName);
    this.props
      .getPage(pageName)
      .then(() => {
        this.setState({ unauthorized: false, notFound: false });
      })
      .catch(err => {
        const status = _.get(err, 'response.status');

        if (status === 401) {
          // Unauthorized.... do something
          toastr.error('Unauthorized');
          this.setState({ unauthorized: true });
        } else if (status === 404) {
          this.setState({ notFound: true });
        } else {
          toastr.error('Error communicating with server');
        }
      });
  }

  getRouteType() {
    return this.getPageConfigValue('RouteType');
  }

  getPageType() {
    if (this.isInvalid()) {
      return <FileNotFound message="Page Error :(" />;
    }

    const { match, pageConfig, pageName, location } = this.props;
    const { params } = match;

    const query = queryString.parse(location.search) || {};

    if (this.getPageConfigValue('Disabled')) {
      return (
        <FileNotFound message="Page Disabled :(" terminal={this.getPageConfigValue('Terminal')} pageName={pageName} />
      );
    }

    if (!this.checkConditions()) {
      return <FileNotFound message={`${pageName} requires additional configuration or is disabled`} />;
    }

    if (this.isLoading()) {
      return <LoadingPage />;
    }

    switch (this.getRouteType()) {
      case 'viewList':
        return <List name={pageName} page={pageConfig} action={params.action} itemId={params.id} query={query} />;

      case 'viewReport':
      case 'viewWebSolutions':
        return <Report name={pageName} page={pageConfig} />;

      case 'viewPage':
        return <Item name={pageName} page={pageConfig} query={query} />;

      case 'viewDb':
        return <Db name={pageName} page={pageConfig.page} action={params.action} itemId={params.id} query={query} />;

      case 'viewCalendar':
        return <CalendarLoader name={pageName} page={pageConfig} params={params} query={query} />;

      case 'viewSignature':
        return <SignatureLoader name={pageName} page={pageConfig} documentId={params.id} />;

      case 'shopKeeper':
        // seedNum is PIN1=CUST#, PRO1/SMM3=RO#, SRE2=EST#
        return <Shopkeeper seedNum={params.id} confirmationNum={params.action} />;

      case 'timeClockEmployees':
        return <TimeClock clockType="employees" />;

      case 'timeClockMechanics':
        return <TimeClock clockType="mechanics" />;

      case 'archive':
        return <Archive name={pageName} />;

      case 'partRequestKanban':
        return <PartRequestKanban />;

      case 'repairOrderKanban':
        return <RepairOrderBoard />;

      case 'query':
        return (
          <QueryListContainer
            name={pageName}
            headers={this.getPageConfigValue('Headers')}
            filter={this.getPageConfigValue('Filter')}
            serverSide={this.getPageConfigValue('List.serverSide')}
          />
        );

      case 'printLog':
        return <PrintLogRouter />;

      case 'payment':
        return <PaymentRouter />;

      case 'userAdmin':
        return <UserAdminRouter />;

      case 'config':
        return <ConfigRouter />;

      case 'editTime':
        return <TimeCard />;

      case 'salesdash':
        return <SalesDashboard />;

      case 'redisList':
        return <RedisList />;

      default:
        return <FileNotFound message={`${pageName} is not yet supported`} />;
    }
  }

  isLoading() {
    return this.props.isFetching || this.props.pageName !== this.props.match.params.pageName.toUpperCase();
  }

  isHidden() {
    return this.getPageConfigValue('Hidden');
  }

  isInvalid() {
    return !this.props.pageConfig || !Object.keys(this.props.pageConfig).length;
  }

  is404() {
    return !this.isLoading() && !this.getRouteType() && this.state.notFound;
  }

  is401() {
    return !this.isLoading() && !this.getRouteType() && this.state.unauthorized;
  }

  showFooter() {
    const routeType = this.getRouteType();

    if (routeType === 'viewPage') {
      return false;
    }

    if ((routeType === 'viewList' || routeType === 'viewDb') && this.props.match.params.action) {
      return true;
    }

    return false;
  }

  showHeader() {
    const routeType = this.getRouteType();

    if (routeType === 'viewCalendar') {
      return false;
    }

    return true;
  }

  updateHistory() {
    const { pageName } = this.props;

    const historyKey = 'user.page.history';

    let history;
    try {
      history = JSON.parse(localStorage.getItem(historyKey)) || [];
    } catch (err) {
      history = [];
    }

    // remove pageName from list if already exists
    _.pull(history, pageName);
    history.unshift(pageName);

    localStorage.setItem(historyKey, JSON.stringify(history));
  }

  render() {
    const { params } = this.props.match;

    if (this.isLoading()) {
      return (
        <PageContainer>
          <LoadingPage />
          <Footer />
        </PageContainer>
      );
    }

    if (this.is404()) {
      return (
        <PageContainer>
          <FileNotFound />
          <Footer />
        </PageContainer>
      );
    }

    if (this.is401()) {
      return (
        <PageContainer>
          <FileNotFound message="Access Denied" />;
          <Footer />
        </PageContainer>
      );
    }

    if (this.isInvalid()) {
      return <FileNotFound message="Page Error :(" />;
    }

    if (this.isHidden()) {
      return (
        <PageContainer>
          <FileNotFound />;
          <Footer />
        </PageContainer>
      );
    }

    const { pageName } = this.props;

    // TODO: What if pageData is bad?
    const related = this.getPageConfigValue('Related');
    const title = this.getPageConfigValue('Title');
    const terminal = this.getPageConfigValue('Terminal');
    const disableSidebar = this.getPageConfigValue('Sidebar') === false;
    const isListItem = /view(List|Db)/.test(this.getRouteType()) && (!!params.id || params.action === 'new');
    const isList = /view(List|Db)/.test(this.getRouteType()) && !params.id && params.action !== 'new';
    const info = isListItem || this.getRouteType() === 'viewPage';

    const layoutOptions = this.getPageConfigValue('LayoutOptions');
    const contained = layoutOptions && layoutOptions.includes('contained');
    const containedtabs = layoutOptions && layoutOptions.includes('containedtabs');

    const className = classNames(`page-${pageName}`, {
      contained: contained || containedtabs,
      containedtabs,
      list: isList,
    });

    const itemId = params.id ? decodeURIComponent(params.id) : null;

    return (
      <PageContainer className={className} disableSidebar={disableSidebar}>
        <Header
          hasDocs={!!this.getPageConfigValue('Docs.Blaze')}
          pageName={pageName}
          showHeader={this.showHeader()}
          showInfo={info}
          showTerminal={terminal}
          title={title}
          itemId={itemId}
          action={params.action}
          related={related}
        />
        {this.getPageType()}
        <Footer show={this.showFooter()} />
      </PageContainer>
    );
  }
}

Page.propTypes = {
  match: PropTypes.object,
  isFetching: PropTypes.bool,
  pageName: PropTypes.string,
  pageConfig: PropTypes.object,
  location: PropTypes.object,

  // redux props
  config: PropTypes.object,
  getPage: PropTypes.func,
  requestPage: PropTypes.func,
  unmountPage: PropTypes.func,
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators({ getPage, requestPage, unmountPage }, dispatch);
};

const mapStateToProps = state => {
  return {
    config: state.app.config,
    isFetching: state.page.isFetching,
    pageConfig: state.page.data,
    pageName: state.page.pageName,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Page);
